/** The running app in a sandboxed iframe; its controls portal into the host's toolbar slot. */
import {useCallback, useEffect, useMemo, useRef, useState} from "react"

import {
    SANDBOX_FLAGS,
    buildRunFrame,
    isFrameNavigated,
    type HtmlAppHost,
    type HtmlAppHostError,
    type PickedPayload,
} from "@agenta/entities/drive"
import {useLocalize} from "@agenta/i18n"
import type {PickerRect} from "@agenta/shared/previewPicker"
import {
    frameRectToRoot,
    PreviewCommentLayer,
    toPinQueries,
    usePreviewComments,
    type PreviewPick,
    type PreviewPinRect,
    type PreviewRect,
} from "@agenta/ui/quote-selection"
import {Button, SimpleTooltip as Tooltip, Skeleton, cn} from "@agenta/ui/ui"
import {ArrowsClockwise, CaretLeft, Copy, LockSimple, Warning} from "@phosphor-icons/react"
import {createPortal} from "react-dom"

import {ROW_ICON_BTN} from "../DriveHeader"
import {previewTarget, usePreviewCommentMode, usePreviewCommentSession} from "../previewCommentMode"

import {
    assembleRunDocument,
    dirOf,
    isExternalUrl,
    resolveRel,
    withinDir,
    type AssembleIo,
} from "./assemble"
import {resolveKitTokens} from "./kit"

/** Kit tokens read off the host document's root (lane E's resolver); `{}` without a DOM. */
export const resolveHostKitTokens = (): Record<string, string> =>
    typeof document === "undefined" ? {} : resolveKitTokens(document.documentElement)

/** Re-run `cb` when the root theme changes. The host stamps the theme as the `.dark` CLASS on
 * `<html>` (`useThemeMode` in `@agenta/ui/theme`), so `class` is the primary attribute watched;
 * `data-theme` and `style` (color-scheme) ride along, and the OS query covers "system". */
const useRootThemeChange = (cb: () => void) => {
    const latest = useRef(cb)
    latest.current = cb
    useEffect(() => {
        if (typeof document === "undefined") return
        const observer = new MutationObserver(() => latest.current())
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class", "data-theme", "style"],
        })
        const query = window.matchMedia?.("(prefers-color-scheme: dark)")
        const onScheme = () => latest.current()
        query?.addEventListener("change", onScheme)
        return () => {
            observer.disconnect()
            query?.removeEventListener("change", onScheme)
        }
    }, [])
}

// ---------------------------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------------------------

export interface RunViewProps {
    host: HtmlAppHost
    /** App dir, mount-relative (`""` for the root). */
    dir: string
    /** Mount-relative path of the entry file. */
    entryPath: string
    /** The entry file's text (the caller owns the query). */
    entryContent: string
    io: AssembleIo | null
    /** Kit stylesheet; null when the manifest disables the kit. */
    kitCss: string | null
    bridgeStub?: string
    /** Fallback document title (manifest name, else the file name) when the page has none. */
    title?: string
    /** Token resolver; defaults to {@link resolveHostKitTokens}. */
    resolveTokens?: () => Record<string, string>
    /** Tab shown/hidden → `host.setVisible`. */
    visible?: boolean
    /** Paths that changed underneath the app (from `useChangedHint`); shown as a pill. */
    changedPaths?: string[]
    /** Called after Refresh (the caller may refetch the entry text and clear the hint). */
    onReload?: () => void
    /** Navigation OUTSIDE the app dir: open that file in the drive. */
    onNavigate?: (path: string) => void
    /** Maps a mount-relative path to the presented one for `onNavigate` (default: identity). */
    toDisplayPath?: (path: string) => string
    /** The host's toolbar slot the controls portal into; without one they sit above the app. */
    controlsContainer?: HTMLElement | null
    /** Mount the app lives in; a preview comment records it. */
    mountId?: string
    /** Set while the app is read only but needs to write: shows "Read only · Allow editing". */
    onAllowEditing?: () => void
    className?: string
}

const describeError = (e: HtmlAppHostError): string => {
    if (e.kind === "script") return `Script error${e.line ? ` (line ${e.line})` : ""}: ${e.message}`
    const where = [e.method, e.path].filter(Boolean).join(" ")
    return `${where ? `${where}: ` : ""}${e.message}`
}

const toPick = ({element, text, rect}: PickedPayload, at: PreviewRect): PreviewPick => ({
    anchor: {
        type: "element",
        format: "html",
        selector: element.selector,
        tag: element.tag,
        openTag: element.openTag,
        point: element.point,
    },
    text,
    rect: at,
})

export function RunView({
    host,
    dir,
    entryPath,
    entryContent,
    io,
    kitCss,
    bridgeStub,
    title,
    resolveTokens = resolveHostKitTokens,
    visible = true,
    changedPaths = [],
    onReload,
    onNavigate,
    toDisplayPath = (p) => p,
    controlsContainer,
    mountId,
    onAllowEditing,
    className,
}: RunViewProps) {
    const t = useLocalize()
    const frameRef = useRef<HTMLIFrameElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    const [currentPath, setCurrentPath] = useState(entryPath)
    const [backStack, setBackStack] = useState<string[]>([])
    const [doc, setDoc] = useState<string | null>(null)
    const [frameKey, setFrameKey] = useState(0)
    /** Bumped per assembled page, so every page gets a fresh iframe and a fresh first load. */
    const [docVersion, setDocVersion] = useState(0)
    /** The app tried to load another document; it stays stopped until Refresh. */
    const [stopped, setStopped] = useState(false)
    const attachedFrameRef = useRef<HTMLIFrameElement | null>(null)
    const [errors, setErrors] = useState<string[]>([])
    const [errorsOpen, setErrorsOpen] = useState(false)
    const [copied, setCopied] = useState(false)

    const pushError = useCallback((message: string) => {
        setErrors((prev) => (prev[prev.length - 1] === message ? prev : [...prev, message]))
    }, [])

    // Errors from the host (bridge rejections + script errors forwarded by the stub).
    useEffect(() => host.onError((e) => pushError(describeError(e))), [host, pushError])

    // Assemble the current page. The entry uses the text the caller already holds; a sibling
    // page is fetched through `io`. `frameKey` in the deps makes Refresh re-assemble.
    useEffect(() => {
        let alive = true
        setDoc(null)
        const load = async (): Promise<string | null> => {
            if (currentPath === entryPath) return entryContent
            return io ? io.fetchText(currentPath) : null
        }
        void load().then(async (html) => {
            if (!alive) return
            if (html == null) {
                pushError(`Page not found in the app folder: ${currentPath}`)
                setDocVersion((v) => v + 1)
                setDoc("")
                return
            }
            const result = await assembleRunDocument(html, {
                dir,
                io,
                tokens: resolveTokens(),
                kitCss,
                bridgeStub,
                title,
            })
            if (!alive) return
            result.errors.forEach(pushError)
            setDocVersion((v) => v + 1)
            setDoc(result.html)
        })
        return () => {
            alive = false
        }
        // resolveTokens is read at assemble time only; theme changes go through setTheme below.
    }, [
        currentPath,
        entryPath,
        entryContent,
        io,
        dir,
        kitCss,
        bridgeStub,
        title,
        frameKey,
        pushError,
    ])

    const frameDoc = useMemo(() => (doc == null ? null : buildRunFrame(doc)), [doc])

    const stopApp = useCallback(() => {
        host.detach()
        setStopped(true)
        pushError("The app tried to load another page and was stopped. Refresh to restart it.")
    }, [host, pushError])

    // Attach on the frame's FIRST load only; detach when the view goes away. The hello goes to
    // "*" (the frame is an opaque origin), so a later load means someone else's document would
    // get the bridge port. Every page we assemble gets a new iframe, so a second load on the same
    // element is never ours.
    useEffect(() => () => host.detach(), [host])
    const onFrameLoad = useCallback(() => {
        const frame = frameRef.current
        if (!frame) return
        if (attachedFrameRef.current === frame) {
            stopApp()
            return
        }
        attachedFrameRef.current = frame
        host.attach(frame)
    }, [host, stopApp])

    // The wrapper reports the app's own frame loading a second document (see `buildRunFrame`).
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            const wrapper = frameRef.current?.contentWindow
            if (!wrapper || event.source !== wrapper || !isFrameNavigated(event.data)) return
            stopApp()
        }
        window.addEventListener("message", onMessage)
        return () => window.removeEventListener("message", onMessage)
    }, [stopApp])

    useEffect(() => host.setVisible(visible), [host, visible])

    useRootThemeChange(() => host.setTheme(resolveTokens()))

    // Navigation from the app: inside the dir → show it here; outside → the drive.
    useEffect(
        () =>
            host.onNav((href) => {
                const clean = href.split(/[?#]/)[0]
                if (!clean || isExternalUrl(clean)) return
                const target = resolveRel(dirOf(currentPath), clean)
                if (withinDir(dir, target)) {
                    setBackStack((stack) => [...stack, currentPath])
                    setCurrentPath(target)
                    setErrors([])
                } else {
                    onNavigate?.(toDisplayPath(target))
                }
            }),
        [host, currentPath, dir, onNavigate, toDisplayPath],
    )

    // Preview comments: the page on screen is the comment's target.
    const sessionId = usePreviewCommentSession()
    const {enabled: commenting, setEnabled: setCommenting} = usePreviewCommentMode()
    const [pick, setPick] = useState<PreviewPick | null>(null)
    const [pinRects, setPinRects] = useState<PreviewPinRect[]>([])
    const displayPath = toDisplayPath(currentPath)
    const target = useMemo(
        () => previewTarget(currentPath, displayPath, mountId),
        [currentPath, displayPath, mountId],
    )
    const comments = usePreviewComments(sessionId, target)
    const pins = useMemo(() => toPinQueries(comments), [comments])

    useEffect(() => host.setPins?.(pins), [host, pins])

    useEffect(() => {
        if (commenting) host.startPick?.()
        else {
            host.stopPick?.()
            setPick(null)
        }
    }, [host, commenting])

    useEffect(() => {
        setPick(null)
        setPinRects([])
    }, [currentPath, frameKey])

    const inRoot = useCallback((rect: PickerRect): PreviewRect | null => {
        const frame = frameRef.current
        const root = rootRef.current
        return frame && root ? frameRectToRoot(rect, frame, root) : null
    }, [])

    useEffect(
        () =>
            host.onPicked?.((picked) => {
                const at = inRoot(picked.rect)
                if (at) setPick(toPick(picked, at))
            }),
        [host, inRoot],
    )
    useEffect(
        () =>
            host.onPinRects?.((rects) =>
                setPinRects(rects.map(({id, rect}) => ({id, rect: rect ? inRoot(rect) : null}))),
            ),
        [host, inRoot],
    )
    useEffect(() => host.onPickCancel?.(() => setCommenting(false)), [host, setCommenting])

    const goBack = useCallback(() => {
        setBackStack((stack) => {
            const prev = stack[stack.length - 1]
            if (prev === undefined) return stack
            setCurrentPath(prev)
            setErrors([])
            return stack.slice(0, -1)
        })
    }, [])

    const reload = useCallback(() => {
        setStopped(false)
        setErrors([])
        setErrorsOpen(false)
        setFrameKey((k) => k + 1)
        onReload?.()
    }, [onReload])

    const copyErrors = useCallback(() => {
        void navigator.clipboard?.writeText(errors.join("\n")).then(() => {
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1200)
        })
    }, [errors])

    const controls = (
        <>
            {backStack.length > 0 ? (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={goBack}
                    aria-label={t("com_entity_drive_app_back_aria")}
                    className="h-6 gap-0.5 px-1.5 text-xs"
                >
                    <CaretLeft weight="bold" className="size-3" />
                    {t("com_entity_drive_app_back")}
                </Button>
            ) : null}

            {onAllowEditing ? (
                <Tooltip title={t("com_entity_drive_app_read_only_tooltip")}>
                    <button
                        type="button"
                        onClick={onAllowEditing}
                        className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-solid border-colorBorder bg-colorFillTertiary px-2 text-[11px] text-colorTextSecondary hover:text-colorText"
                    >
                        <LockSimple weight="bold" className="size-3" />
                        {t("com_entity_drive_app_allow_editing")}
                    </button>
                </Tooltip>
            ) : null}

            {changedPaths.length > 0 ? (
                <button
                    type="button"
                    onClick={reload}
                    title={changedPaths.join("\n")}
                    className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-solid border-colorWarning/40 bg-colorWarningBg px-2 text-[11px] text-colorWarning"
                >
                    {t("com_entity_drive_app_files_changed")}
                </button>
            ) : null}

            {errors.length > 0 ? (
                <button
                    type="button"
                    onClick={() => setErrorsOpen((v) => !v)}
                    aria-expanded={errorsOpen}
                    aria-label={t("com_entity_drive_app_errors", {count: errors.length})}
                    className="inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-solid border-colorError/40 bg-colorErrorBg px-2 text-[11px] text-colorError"
                >
                    <Warning weight="fill" className="size-3" />
                    {errors.length}
                </button>
            ) : null}

            <Tooltip title={t("com_entity_drive_refresh")}>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={reload}
                    aria-label={t("com_entity_drive_refresh")}
                    className={ROW_ICON_BTN}
                >
                    <ArrowsClockwise size={14} />
                </Button>
            </Tooltip>
        </>
    )

    return (
        <div className={cn("flex min-h-0 flex-1 flex-col text-xs", className)}>
            {controlsContainer ? (
                createPortal(controls, controlsContainer)
            ) : (
                <div
                    data-slot="run-controls"
                    className="flex shrink-0 items-center justify-end gap-1 border-0 border-b border-solid border-colorBorderSecondary px-2 py-1"
                >
                    {controls}
                </div>
            )}

            {errorsOpen && errors.length > 0 ? (
                <div
                    data-slot="run-errors"
                    className="flex max-h-40 shrink-0 flex-col gap-1 overflow-auto border-0 border-b border-solid border-colorBorderSecondary bg-colorErrorBg px-2 py-1.5 text-colorError"
                >
                    <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">
                            {t("com_entity_drive_app_errors", {count: errors.length})}
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyErrors}
                            className="h-6 gap-1 px-1.5 text-xs text-colorError"
                        >
                            <Copy className="size-3" />
                            {copied ? t("com_ui_copied") : t("com_ui_copy")}
                        </Button>
                    </div>
                    <ul className="m-0 list-disc pl-4 font-mono text-[11px] leading-4">
                        {errors.map((e, i) => (
                            <li key={`${i}-${e}`} className="break-words">
                                {e}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {frameDoc == null ? (
                <div className="min-h-0 flex-1 p-3">
                    <div className="flex flex-col gap-2">
                        {Array.from({length: 6}).map((_, i) => (
                            <Skeleton key={i} className="h-4 w-full" />
                        ))}
                    </div>
                </div>
            ) : stopped ? (
                <div data-slot="run-stopped" className="min-h-0 flex-1 p-3 text-colorTextSecondary">
                    {t("com_entity_drive_app_stopped")}
                </div>
            ) : (
                // No allow-same-origin: the app is an opaque origin and reaches the drive only over
                // the port the host hands it on load. The srcdoc is the wrapper from
                // `buildRunFrame`, which holds the app in a nested frame that cannot navigate out.
                <div ref={rootRef} className="relative flex min-h-0 flex-1 flex-col">
                    <iframe
                        key={`${frameKey}:${docVersion}`}
                        ref={frameRef}
                        srcDoc={frameDoc}
                        sandbox={SANDBOX_FLAGS}
                        onLoad={onFrameLoad}
                        title={t("com_entity_drive_app_frame")}
                        className="min-h-0 w-full flex-1 border-0 bg-colorBgContainer"
                    />
                    <PreviewCommentLayer
                        rootRef={rootRef}
                        sessionId={sessionId}
                        target={target}
                        pick={pick}
                        onPickConsumed={() => setPick(null)}
                        pinRects={pinRects}
                    />
                </div>
            )}
        </div>
    )
}
